/**
 * Gamificación (kit `gamification-core`) — tarjeta de perfil.
 *
 * Deriva el estado del jugador (XP/nivel/racha/badges) de la COLECCIÓN
 * (`user_collection`) local-first, sin columna de sync nueva:
 * `computePlayerFromCollection` es un reducer puro y determinista → feedback
 * instantáneo (funciona para invitados sin sync). Añadir un producto poseído
 * suma +50 XP y marca el día como "coleccionista" (racha). Gamificación
 * positiva y opcional; ligas privadas por defecto.
 *
 * Respeta `prefers-reduced-motion`: la barra de XP anima con `transition` que
 * el guard global de `global.css` desactiva; no hay animaciones JS.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Flame, Sparkles, Trophy } from "lucide-solid";
import {
  computePlayerFromCollection,
  FAMIDEX_COLLECTION_BADGES,
  getCollectionQuery,
  levelProgress,
  queryKeys,
  STREAK_COLLECTING,
} from "@famidex/shared";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";

/** Título humano por id de badge (i18n reactivo). */
function badgeTitle(t: (d: ReturnType<typeof msg>) => string, id: string): string {
  switch (id) {
    case "first-find": {
      return t(msg`First find`);
    }
    case "ten-finds": {
      return t(msg`Collector`);
    }
    case "fifty-finds": {
      return t(msg`Curator`);
    }
    case "century-finds": {
      return t(msg`Great keeper`);
    }
    case "week-collector": {
      return t(msg`Collecting week`);
    }
    case "month-collector": {
      return t(msg`Month collecting`);
    }
    case "collector-level-5": {
      return t(msg`Level 5`);
    }
    case "collector-level-10": {
      return t(msg`Level 10`);
    }
    default: {
      return id;
    }
  }
}

interface GamificationSectionProps {
  userId: string;
}

export function GamificationSection(props: GamificationSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const collection = createPowerSyncQuery({
    queryKey: () => queryKeys.collection.items(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getCollectionQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const player = createMemo(() => computePlayerFromCollection(collection.data ?? []).state);
  const progress = createMemo(() => levelProgress(player().xp));
  const streak = createMemo(() => player().streaks[STREAK_COLLECTING]?.current ?? 0);
  const owned = createMemo(() => new Set(player().badges));

  const levelLabel = () => {
    const { level } = progress();
    return t(msg`Level ${level}`);
  };
  const xpLabel = () => {
    const { xp } = player();
    return t(msg`${xp} XP`);
  };
  const streakLabel = () => {
    const days = streak();
    return t(msg`${days} day streak`);
  };
  const toNextLabel = () => {
    const xp = progress().xpToNext;
    return t(msg`${xp} XP to next level`);
  };

  return (
    <Card data-testid="gamification-section">
      <CardHeader>
        <div class="flex items-center gap-2">
          <Sparkles class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Progress`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <Show when={!collection.isPending} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
          {/* Nivel + barra de XP */}
          <div class="space-y-2">
            <div class="flex items-end justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary"
                  data-testid="gamification-level"
                >
                  {progress().level}
                </span>
                <div class="flex flex-col">
                  <span class="text-sm font-semibold text-foreground">{levelLabel()}</span>
                  <span class="text-xs text-muted-foreground" data-testid="gamification-xp">
                    {xpLabel()}
                  </span>
                </div>
              </div>
              <Show when={streak() > 0}>
                <span
                  class="flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning"
                  data-testid="gamification-streak"
                >
                  <Flame class="h-3.5 w-3.5" />
                  {streakLabel()}
                </span>
              </Show>
            </div>
            <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${Math.round(progress().ratio * 100)}%` }}
              />
            </div>
            <p class="text-right text-xs text-muted-foreground">{toNextLabel()}</p>
          </div>

          {/* Badges (obtenidos en color, pendientes en gris) */}
          <div>
            <p class="mb-3 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Trophy class="h-4 w-4 text-muted-foreground" />
              {t(msg`Achievements`)}
            </p>
            <div class="flex flex-wrap gap-2">
              <For each={FAMIDEX_COLLECTION_BADGES}>
                {(badge) => {
                  const has = () => owned().has(badge.id);
                  return (
                    <Tooltip>
                      <TooltipTrigger
                        as="div"
                        class="flex h-11 w-11 items-center justify-center rounded-xl border transition-colors"
                        classList={{
                          "border-primary/30 bg-primary/10 text-primary": has(),
                          "border-dashed border-muted-foreground/20 bg-muted/40 text-muted-foreground/40":
                            !has(),
                        }}
                        data-testid={`badge-${badge.id}`}
                        data-earned={has() ? "true" : "false"}
                        aria-label={badgeTitle(t, badge.id)}
                      >
                        <Trophy class="h-5 w-5" />
                      </TooltipTrigger>
                      <TooltipContent>{badgeTitle(t, badge.id)}</TooltipContent>
                    </Tooltip>
                  );
                }}
              </For>
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
